import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmailReportKind, EmailReportTable } from '@/infrastructure/http/metricsApi';
import { EmailReportButton } from '@/presentation/components/reports/EmailReportButton';
import type { ReportEmailPayload } from '@/presentation/components/reports/emailReportPayload';
import { ReportDetailHeader } from '@/presentation/components/reports/ReportDetailHeader';
import { ReportScopeFilters } from '@/presentation/components/reports/ReportScopeFilters';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { useReportScopeFilters } from '@/presentation/hooks/useReportScopeFilters';

/** Cáscara de página de reporte: mismo canvas que Finanzas/Pacientes. */
export const REPORT_PAGE_CLASS = 'panel-page panel-safe-bottom bi-surface';

export interface ReportScopeContext {
  token: string;
  clinicId: string;
  year: number;
  month: number;
  period: string;
  clinicOptions: Array<{ value: string; label: string }>;
  isMultiClinic: boolean;
  /** Empuja KPIs/tablas al PDF del mail (kind=generic). */
  setEmailPayload: (payload: ReportEmailPayload | null) => void;
}

interface ClinicScopedReportProps {
  title: string;
  description?: string;
  hint?: string;
  /** Uno o más permisos; basta con uno. */
  permissions: string[];
  /** Si false, no pide clínica (p. ej. consolidado multi-clínica). */
  requireClinic?: boolean;
  showClinicFilter?: boolean;
  /** Tipo de PDF a generar al enviar por mail. Default: generic. */
  emailKind?: EmailReportKind;
  actions?: ReactNode;
  children: (ctx: ReportScopeContext) => ReactNode;
}

/**
 * Shell común de reportes dedicados: cabecera, filtros de periodo/clínica y gate de permisos.
 */
export function ClinicScopedReport({
  title,
  description,
  hint,
  permissions,
  requireClinic = true,
  showClinicFilter = true,
  emailKind = 'generic',
  actions,
  children,
}: ClinicScopedReportProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { hasPermission } = usePermissions();
  const filters = useReportScopeFilters();
  const [emailPayload, setEmailPayloadState] = useState<ReportEmailPayload>({});

  const canView = permissions.some((key) => hasPermission(key));

  useEffect(() => {
    setEmailPayloadState({});
  }, [filters.period, filters.clinicId, title, emailKind]);

  const setEmailPayload = useCallback((payload: ReportEmailPayload | null) => {
    setEmailPayloadState(payload ?? {});
  }, []);

  if (!canView) {
    return (
      <div className={REPORT_PAGE_CLASS}>
        <ReportDetailHeader title={title} />
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('reports.forbidden.message')}</p>
      </div>
    );
  }

  const ready =
    Boolean(token) &&
    (!requireClinic || Boolean(filters.clinicId)) &&
    (!requireClinic || filters.clinicOptions.length > 0);

  const emailDisabled =
    (emailKind === 'summary' || emailKind === 'caja' || (emailKind === 'generic' && requireClinic)) &&
    !filters.clinicId;

  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      <EmailReportButton
        disabled={emailDisabled || !ready}
        scope={{
          kind: emailKind,
          year: filters.year,
          month: filters.month,
          tenantId: filters.clinicId || undefined,
          title,
          description,
          kpis: emailKind === 'generic' ? emailPayload.kpis : undefined,
          tables: emailKind === 'generic' ? (emailPayload.tables as EmailReportTable[] | undefined) : undefined,
        }}
      />
      {actions}
    </div>
  );

  return (
    <div className={REPORT_PAGE_CLASS}>
      <ReportDetailHeader
        title={title}
        description={description}
        actions={headerActions}
        backTo={filters.hubHref}
      />

      <ReportScopeFilters
        title={t('reports.filters.title')}
        periodLabel={t('reports.filters.period')}
        clinicLabel={t('reports.filters.clinic')}
        period={filters.period}
        onPeriodChange={filters.setPeriod}
        periodOptions={filters.periodOptions}
        clinicId={filters.clinicId}
        onClinicChange={filters.setClinicId}
        clinicOptions={filters.clinicOptions}
        showClinic={showClinicFilter}
        showPeriod={false}
        hint={hint}
      />

      {ready && token ? (
        children({
          token,
          clinicId: filters.clinicId,
          year: filters.year,
          month: filters.month,
          period: filters.period,
          clinicOptions: filters.clinicOptions,
          isMultiClinic: filters.isMultiClinic,
          setEmailPayload,
        })
      ) : requireClinic ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.needsClinic')}</p>
      ) : (
        <LoadingBlock minHeightClassName="min-h-[8rem]" />
      )}
    </div>
  );
}
